import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../../../utils/axiosConfig';
import { useAuth } from '../../../contexts/AuthContext';

// POST /api/orders/payments/initiate/
// Body: { order, amount, payment_method, phone_number }
// payment_method choices: 'ncba' | 'mpesa' | 'cash'

const PaymentPage = () => {
  const [paymentMethod, setPaymentMethod] = useState('ncba');
  const [phone, setPhone]   = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [status, setStatus] = useState(null); // null | 'pending' | 'paid' | 'failed'
  const [paymentId, setPaymentId] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const navigate  = useNavigate();
  const location  = useLocation();
  const { user }  = useAuth();

  const order_id = location.state?.order_id;
  const amount   = location.state?.amount;

  // Pre-fill phone from user profile
  useEffect(() => {
    if (user?.phone_number) setPhone(user.phone_number);
  }, [user]);

  // Redirect if no order
  useEffect(() => {
    if (!order_id) navigate('/dashboard');
  }, [order_id, navigate]);

  // Poll payment status
  useEffect(() => {
    if (!paymentId || status === 'paid' || status === 'failed') return;
    if (pollCount >= 24) { // 2 min max
      setError('Payment timed out. Please check your orders for status.');
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`orders/${order_id}/payment-status/`);
        const s = res.data.payment_status || res.data.status;
        if (s === 'paid' || s === 'Paid' || s === 'completed') {
          setStatus('paid');
        } else if (s === 'failed' || s === 'Failed') {
          setStatus('failed');
          setError('Payment failed. Please try again.');
        } else {
          setPollCount(c => c + 1);
        }
      } catch (_) {
        setPollCount(c => c + 1);
      }
    }, 5000);
    return () => clearTimeout(timer);
  }, [paymentId, pollCount, status, order_id]);

  const handlePay = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        order:          order_id,
        amount:         parseFloat(amount),
        payment_method: paymentMethod,
      };
      if (paymentMethod === 'ncba' || paymentMethod === 'mpesa') {
        payload.phone_number = phone.trim();
      }

      const res = await axios.post('orders/payments/initiate/', payload);
      setPaymentId(res.data.id || res.data.payment_id);
      setStatus('pending');
    } catch (err) {
      setError(
        err.response?.data?.non_field_errors?.[0] ||
        err.response?.data?.order?.[0] ||
        err.response?.data?.error ||
        err.response?.data?.detail ||
        'Payment initiation failed. Please try again.'
      );
    }
    setLoading(false);
  };

  if (status === 'paid') {
    return (
      <div className="page">
        <div className="card center">
          <div className="success-icon">✓</div>
          <h2>Payment Successful!</h2>
          <p className="subtitle">Your order has been confirmed and a rider will be assigned shortly.</p>
          <button onClick={() => navigate('/orders')} className="primary-btn">View My Orders</button>
        </div>
        {styles}
      </div>
    );
  }

  return (
    <div className="page">
      <div className="card">
        <h2>Complete Payment</h2>

        {/* Order summary */}
        <div className="summary-box">
          <div className="s-row"><span>Order ID</span><span>#{order_id}</span></div>
          <div className="s-row total"><span>Amount Due</span><span>KES {amount}</span></div>
        </div>

        {error && <div className="error-box">{error}</div>}

        {status === 'pending' ? (
          <div className="pending-box">
            <div className="spinner" />
            <p>Waiting for payment confirmation...</p>
            <p className="hint">Check your phone for the NCBA/M-Pesa prompt and enter your PIN.</p>
            <button onClick={() => { setStatus(null); setPaymentId(null); setPollCount(0); }} className="back-btn" style={{marginTop:16}}>
              Try Again
            </button>
          </div>
        ) : (
          <form onSubmit={handlePay}>
            {/* Payment method */}
            <label className="lbl">Payment Method</label>
            <div className="method-options">
              {[
                { value: 'ncba',  label: 'NCBA Mobile', icon: '🏦' },
                { value: 'mpesa', label: 'M-Pesa',      icon: '📱' },
                { value: 'cash',  label: 'Cash',         icon: '💵' },
              ].map(m => (
                <div
                  key={m.value}
                  className={`method-card ${paymentMethod === m.value ? 'selected' : ''}`}
                  onClick={() => setPaymentMethod(m.value)}
                >
                  <span className="method-icon">{m.icon}</span>
                  <span>{m.label}</span>
                </div>
              ))}
            </div>

            {/* Phone number (required for ncba/mpesa) */}
            {(paymentMethod === 'ncba' || paymentMethod === 'mpesa') && (
              <>
                <label className="lbl">Phone Number *</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                  placeholder="e.g. 0712345678"
                  required
                  className="inp"
                />
                <p className="hint">You will receive a payment prompt on this number.</p>
              </>
            )}

            {paymentMethod === 'cash' && (
              <div className="info-box">
                Pay cash to the rider upon delivery. Order will be confirmed after payment.
              </div>
            )}

            <button type="submit" disabled={loading} className="primary-btn">
              {loading ? 'Processing...' : `Pay KES ${amount}`}
            </button>
          </form>
        )}
      </div>

      {styles}
    </div>
  );
};

const styles = (
  <style>{`
    .page{min-height:100vh;background:#f3f4f6;display:flex;align-items:flex-start;justify-content:center;padding:40px 20px;font-family:Arial,sans-serif}
    .card{background:white;border-radius:12px;padding:32px;max-width:480px;width:100%;box-shadow:0 2px 16px rgba(0,0,0,.08)}
    .card.center{text-align:center}
    h2{font-size:22px;font-weight:bold;color:#111827;margin-bottom:20px}
    .subtitle{color:#6b7280;font-size:14px;margin-bottom:24px}
    .success-icon{font-size:56px;color:#10b981;margin-bottom:16px}
    .summary-box{background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:14px 16px;margin-bottom:20px}
    .s-row{display:flex;justify-content:space-between;font-size:14px;color:#374151;margin-bottom:6px}
    .s-row.total{font-weight:bold;font-size:16px;color:#111827;border-top:1px solid #e5e7eb;padding-top:8px;margin-top:4px}
    .error-box{background:#fee2e2;border:1px solid #f87171;color:#b91c1c;padding:10px 14px;border-radius:6px;margin-bottom:16px;font-size:14px}
    .lbl{display:block;font-size:13px;font-weight:500;color:#374151;margin-bottom:6px}
    .method-options{display:grid;grid-template-columns:repeat(3,1fr);gap:10px;margin-bottom:20px}
    .method-card{border:2px solid #e5e7eb;border-radius:8px;padding:12px 8px;text-align:center;cursor:pointer;font-size:13px;color:#374151;transition:all .2s}
    .method-card:hover{border-color:#93c5fd}
    .method-card.selected{border-color:#3b82f6;background:#eff6ff;color:#1d4ed8;font-weight:600}
    .method-icon{display:block;font-size:22px;margin-bottom:4px}
    .inp{width:100%;padding:10px 14px;border:1px solid #d1d5db;border-radius:6px;font-size:15px;background:white;box-sizing:border-box;margin-bottom:8px}
    .inp:focus{outline:none;border-color:#3b82f6}
    .hint{font-size:12px;color:#6b7280;margin-bottom:14px}
    .info-box{background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:12px 14px;font-size:14px;color:#1e40af;margin-bottom:16px}
    .pending-box{text-align:center;padding:20px 0}
    .pending-box p{color:#374151;margin-bottom:8px;font-size:15px}
    .spinner{width:40px;height:40px;border:4px solid #e5e7eb;border-top:4px solid #3b82f6;border-radius:50%;animation:spin 1s linear infinite;margin:0 auto 20px}
    @keyframes spin{to{transform:rotate(360deg)}}
    .primary-btn{width:100%;padding:13px;background:#3b82f6;color:white;border:none;border-radius:50px;font-size:16px;font-weight:500;cursor:pointer;transition:background .2s;margin-top:4px}
    .primary-btn:hover{background:#2563eb}.primary-btn:disabled{background:#9ca3af;cursor:not-allowed}
    .back-btn{padding:10px 24px;border:1px solid #d1d5db;border-radius:50px;background:white;color:#374151;font-size:14px;cursor:pointer}
  `}</style>
);

export default PaymentPage;
